import React, { useEffect, useRef, useCallback } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Form, useFormikContext } from "formik";
import { Box, Heading, Text, Button, Icon, Divider, VStack } from "@chakra-ui/react";
import { IoCloudUploadSharp } from "react-icons/io5";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useHistory } from "react-router-dom";
import { equals } from "ramda";

import { toRegionOptions, localizedRegionsFactory, sortCountries } from "utils/regions";
import { useAnalytics } from "utils";
import { SearchableSelectField } from "components/Form";
import { SelectFieldStandard } from "components/Form/Select";

import DocumentUploadFields from "./DocumentUploadFields";
import SupportingDocuments from "./SupportingDocuments";

export default function AdditionalDocuments({
  config: { countriesNotRequiringAddressDocuments = [] },
  onUpdateTouchedFields,
  onShowRetryErrMessage,
  isRetry,
}) {
  const { formatMessage } = useIntl();
  const {
    values, isSubmitting, isValid, setFieldValue, handleSubmit, touched, initialValues
  } = useFormikContext();
  const history = useHistory();
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const regions = sortCountries(localizedRegionsFactory(formatMessage));
  const regionsAsOptions = toRegionOptions(regions);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("KYC", "KYC_AdditionalDocumentationForm_Viewed");
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired]);

  const requiresProofOfAddress = !countriesNotRequiringAddressDocuments.includes(values.country);

  // On mount, we check the value of "Country/Region of residence" and update the
  // "Document issuing country" field, if it is still undefined
  useEffect(() => {
    if (!values.documents.document_country) {
      setFieldValue("documents.document_country", values.country, false);
    }
    if (requiresProofOfAddress) {
      setFieldValue("documents.is_address_on_document", "no");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSelectedDocumentType =
    !!values.documents.document_type && values.documents.document_type.length > 0;

  const formFields = [];
  if (hasSelectedDocumentType) {
    formFields.push([
      <DocumentUploadFields key="document-upload-fields" />,
      <SearchableSelectField
        key="document-issuing-country"
        label={formatMessage({ defaultMessage: "Document issuing country", description: "Select label" })}
        name="documents.document_country"
        options={regionsAsOptions}
      />,
    ]);

    if (requiresProofOfAddress) {
      formFields.push(<SupportingDocuments key="supporting-documents" />);
    }
  }

  const submit = useCallback(
    (e) => {
      e.preventDefault();
      const valuesSameAsInitialValues = equals(values, initialValues);
      if (valuesSameAsInitialValues && isRetry) {
        if (onShowRetryErrMessage) onShowRetryErrMessage();
        return;
      }
      if (onUpdateTouchedFields) {
        // keep track of which fields were touched
        onUpdateTouchedFields(touched);
      }
      handleSubmit();
    },
    [
      handleSubmit,
      onUpdateTouchedFields,
      touched,
      values,
      initialValues,
      onShowRetryErrMessage,
      isRetry,
    ]
  );

  return (
    <Form onSubmit={submit}>
      <Box px={4} pb={6}>
        <Heading mb={2}>
          <FormattedMessage defaultMessage="A few more things" description="Form header" />
        </Heading>
        <Text fontSize="sm" color="secondaryText">
          <FormattedMessage
            defaultMessage="You'll be required to submit a few documents in order to verify your identity. Select from the options below to get started."
            description="User instructions"
          />
        </Text>
      </Box>

      <Divider />

      <Box p={4}>
        <VStack alignItems="stretch" spacing={6}>
          <SelectFieldStandard
            name="documents.document_type"
            label={formatMessage({ defaultMessage: "Document", description: "Dropdown label" })}
            placeholder={formatMessage({ defaultMessage: "Select a document", description: "Select placeholder" })}
            options={[
              {
                label: formatMessage({
                  defaultMessage: "Driver's license",
                  description: "Dropdown option",
                }),
                value: "DL",
              },
              {
                label: formatMessage({
                  defaultMessage: "Identity card",
                  description: "Dropdown option",
                }),
                value: "IC",
              },
              {
                label: formatMessage({
                  defaultMessage: "Passport",
                  description: "Dropdown option",
                }),
                value: "PP",
              },
              {
                label: formatMessage({
                  defaultMessage: "Residence permit",
                  description: "Dropdown option",
                }),
                value: "RP",
              },
            ]}
          />

          {formFields}

          <VStack>
            <Button
              type="submit"
              isDisabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              loadingText={formatMessage({
                defaultMessage: "Uploading...",
                description: "loading text",
              })}
              size="lg"
              rightIcon={<Icon as={IoCloudUploadSharp} />}
              width="full"
            >
              <FormattedMessage defaultMessage="Upload" description="Button label" />
            </Button>

            <Button
              onClick={() => {
                history.goBack();
              }}
              isDisabled={isSubmitting}
              size="md"
              leftIcon={<ArrowBackIcon />}
              variant="outline"
              width="full"
            >
              <FormattedMessage defaultMessage="Go back" description="Button label" />
            </Button>
          </VStack>
        </VStack>
      </Box>
    </Form>
  );
}
